import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import ubicacionService from '../services/ubicacionService';
import { IUbicacion } from '../interfaces/Ubicacion';

interface UbicacionSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (ubicacion: IUbicacion) => void;
}

const UbicacionSearch: React.FC<UbicacionSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [ubicaciones, setUbicaciones] = useState<IUbicacion[]>([]);
    const [ubicacionesFiltradas, setUbicacionesFiltradas] = useState<IUbicacion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadUbicaciones = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const ubicacionesArray = await ubicacionService.getUbicaciones();
            setUbicaciones(ubicacionesArray);
            setUbicacionesFiltradas(ubicacionesArray);
            if (ubicacionesArray.length === 0) {
                setError('No se encontraron ubicaciones.');
            }
        } catch (err: any) {
            setError(`Error al cargar ubicaciones: ${err.message || 'Error desconocido'}`);
            setUbicaciones([]);
            setUbicacionesFiltradas([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = useCallback(() => {
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            setUbicacionesFiltradas(ubicaciones);
            setError(null);
            return;
        }

        const trimmedLower = trimmed.toLowerCase();
        // Buscar por número de ubicación o nombre
        const filtered = ubicaciones.filter(u => {
            const nroUbicacion = String(u.nroUbicacion).includes(trimmed);
            const nombre = u.nombreUbicacion?.toLowerCase().includes(trimmedLower);
            return nroUbicacion || nombre;
        });

        setUbicacionesFiltradas(filtered);
        if (filtered.length === 0) {
            setError('No se encontraron ubicaciones.');
        } else {
            setError(null);
        }
    }, [searchTerm, ubicaciones]);

    const handleSelect = (ubicacion: IUbicacion) => {
        onSelect(ubicacion);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setUbicacionesFiltradas(ubicaciones);
        setError(null);
        onClose();
    };

    // Cargar ubicaciones cuando se abre el diálogo
    useEffect(() => {
        if (open) {
            loadUbicaciones();
        } else {
            setSearchTerm('');
            setUbicaciones([]);
            setUbicacionesFiltradas([]);
            setError(null);
        }
    }, [open, loadUbicaciones]);

    // Filtrar cuando cambia el término de búsqueda
    useEffect(() => {
        if (open && ubicaciones.length > 0) {
            handleSearch();
        }
    }, [open, searchTerm, ubicaciones, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Ubicación
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de ubicación o nombre"
                        inputProps={{ 'aria-label': 'Buscar ubicación' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && ubicacionesFiltradas.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {ubicacionesFiltradas.map((ubicacion) => (
                                        <Card 
                                            key={ubicacion.nroUbicacion} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(ubicacion)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Nro: {ubicacion.nroUbicacion}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(ubicacion);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {ubicacion.nombreUbicacion?.trim() || '-'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nro. Ubicación</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ubicacionesFiltradas.map((ubicacion) => (
                                            <TableRow 
                                                key={ubicacion.nroUbicacion} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(ubicacion)}
                                            >
                                                <TableCell>{ubicacion.nroUbicacion}</TableCell>
                                                <TableCell>{ubicacion.nombreUbicacion?.trim() || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(ubicacion);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && ubicacionesFiltradas.length === 0 && !error && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron ubicaciones
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UbicacionSearch;

